import { householdBudgetCents } from './householdShare';
import type { Planning } from './purchasePlanning';
import { drawBridgeCents } from './loanDrawdown';
import { hasCostRange } from './costEvidence';
import feeTemplates from '../../../../property-workflow/acquisition-fees.json';

export type AcquisitionPayments = { stages: { month: number | null; amount: number | null }[] };
export const emptyAcquisitionPayments = (): AcquisitionPayments => ({ stages: [{ month: null, amount: null }] });
const exact = (value: object, keys: string[]) => Object.keys(value).sort().join(',') === [...keys].sort().join(',');
const money = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 100000000
  && value === Math.round(value * 100) / 100;
export function validAcquisitionPayments(value: unknown): value is AcquisitionPayments {
  if (!value || typeof value !== 'object' || !exact(value, ['stages'])) return false;
  const stages = (value as AcquisitionPayments).stages;
  return Array.isArray(stages) && stages.length <= 30 && stages.every(stage => stage && typeof stage === 'object' && exact(stage, ['month', 'amount'])
    && (stage.month === null || Number.isInteger(stage.month) && stage.month >= 0 && stage.month <= 359)
    && (stage.amount === null || money(stage.amount)));
}
/** Same upper initial budget as financeProjection: allowance OR itemized fees,
 * plus first-year work. Round the complete budget once, preserving old prices. */
export function acquisitionBudgetCents(planning: Planning): number | null {
  const f = planning.finance;
  if (!money(f.purchasePrice) || !f.budgetReviewed || !Number.isFinite(f.purchaseFeesPercent) || f.purchaseFeesPercent < 0 || f.purchaseFeesPercent > 100) return null;
  let works = 0, fees = f.purchasePrice * f.purchaseFeesPercent / 100;
  for (const cost of planning.costs.filter(c => c.category === 'inspection' || c.category === 'repair')) {
    const revision = cost.revisions.at(-1);
    if (!hasCostRange(revision)) return null;
    if (cost.dueMonth < 12) works += revision.high;
  }
  const items = planning.costs.filter(c => c.category === 'fees');
  if ((f.feeMode ?? (items.length ? 'itemized' : 'allowance')) === 'itemized') {
    if (feeTemplates.some(template => !items.some(c => c.feeKind === template.id))) return null;
    fees = 0;
    for (const cost of items) {
      const revision = cost.revisions.at(-1);
      if (!hasCostRange(revision)) return null;
      fees += revision.high;
    }
  }
  return householdBudgetCents(Math.round((f.purchasePrice + fees + works) * 100),f);
}
export function readyAcquisitionPayments(planning: Planning) {
  const f = planning.finance, schedule = f.acquisitionPayments;
  if (schedule === undefined || !validAcquisitionPayments(schedule) || !schedule.stages.length) return null;
  const budget = acquisitionBudgetCents(planning);
  if (budget === null || !money(f.equity) || !money(f.retainedReserve) || f.retainedReserve > f.equity) return null;
  const payments = Array<number>(360).fill(0), draws = Array<number>(360).fill(0);
  for (const stage of schedule.stages) {
    if (stage.month === null || stage.amount === null) return null;
    payments[stage.month] += Math.round(stage.amount * 100);
  }
  if (payments.reduce((a, b) => a + b, 0) !== budget) return null;
  const chosen = planning.requests.filter(r => f.financingMode === 'combined' ? f.selectedRequestIds?.includes(r.id) : f.selectedRequestId === r.id);
  if (f.financingMode === 'combined' ? !chosen.length || chosen.length !== f.selectedRequestIds?.length : !!f.selectedRequestId && !chosen.length) return null;
  let principal = 0;
  for (const request of chosen) {
    if (!money(request.loanAmount) || drawBridgeCents(request) === null) return null;
    const amount = Math.round(request.loanAmount * 100);
    principal += amount;
    if (request.drawdown === undefined) draws[0] += amount;
    else for (const stage of request.drawdown.stages) {
      if (!money(stage.amount)) return null;
      draws[stage.month!] += Math.round(stage.amount * 100);
    }
  }
  const equity = Math.round(f.equity * 100) - Math.round(f.retainedReserve * 100);
  if (!chosen.length) { principal = Math.max(0, budget - equity); draws[0] = principal; }
  if (principal + equity !== budget) return null;
  let paid = 0, drawn = 0;
  for (let month = 0; month < 360; month++) {
    paid += payments[month]; drawn += draws[month];
    // Never release unused/earmarked loan proceeds to other purchases.
    if (drawn > paid) return null;
  }
  return { budgetCents: budget, contributionCents: payments[0] - draws[0], deferredCents: budget - payments[0], paymentsCents: payments };
}
